import { useEffect, useMemo, useState } from "react";
import { App, Button, Empty, Input, Modal, Radio, Segmented, Select, Spin, Tag, Tooltip, Typography } from "antd";
import {
  TbAlertTriangle,
  TbCheck,
  TbChecks,
  TbChevronDown,
  TbChevronRight,
  TbClock,
  TbLayoutKanban,
  TbMinus,
  TbPlayerSkipForward,
  TbUserCircle,
  TbUserPlus,
} from "react-icons/tb";
import { ApiService } from "api/api.service";
import { TaskBoardListItemDto } from "dtos/tasks/task-board.dto";
import { MemberKind, MemberRole, MemberTarget, TaskItemMember } from "dtos/tasks/task-template.dto";
import { T_MongoId } from "types/_utils.types";
import { Resource } from "dtos/stage.dto";
import { accentTag, neutralTag, taskUi, Tokens } from "components/TaskBoard/design";
import { AssignableUser, TEAM_ROLES } from "pages/ApplicationItem/TeamSection";
import { BoardSyncBanner } from "./Banner";
import { describeDue, dueState } from "pages/TaskTemplate/deadline";
import { getInitials } from "utils/functions";
import { TaskStatus } from "dtos/tasks/template.dto";
import { TaskDrawer } from "./TaskDrawer";

/**
 * Instance-side shapes. These belong in dtos/tasks/task-board.dto.ts once the
 * view endpoint returns resolved assignees and the outcome list.
 */
type ResolvedAssignee = { _id: T_MongoId; name: string; email?: string };
type TaskMemberInstance = TaskItemMember & { assignee?: ResolvedAssignee };
type TaskOutcome = { _id: T_MongoId; name: string; description?: string };

type BoardTask = {
  _id: T_MongoId;
  code: string;
  name: string;
  description?: string;
  status: TaskStatus;
  order: number;
  dueAt?: Date;
  members: TaskMemberInstance[];
  outcomes?: TaskOutcome[];
  selectedOutcome?: T_MongoId;
  skipReason?: string;
  subtasks?: BoardTask[];
};

type BoardGroup = { _id: T_MongoId; name: string; code: string; tasks: BoardTask[] };
type BoardView = { _id: T_MongoId; name: string; groups: BoardGroup[] };

type StatusCounts = { done: number; skipped: number; open: number; total: number };
type RowFilter = "open" | "done" | "all";

const DONE_COLOR = "#16a34a";
const SKIPPED_COLOR = "#c6c6d0";
const OPEN_COLOR = "#dcdce4";

const STATUS_META: Record<TaskStatus, { label: string; style: Tokens }> = {
  [TaskStatus.PENDING]: { label: "Not started", style: neutralTag },
  [TaskStatus.ACTIVE]: { label: "In progress", style: accentTag },
  [TaskStatus.COMPLETE]: {
    label: "Complete",
    style: { ...neutralTag, color: "#15803d", background: "#ecfdf3", borderColor: "#c3e4ce" },
  },
  [TaskStatus.SKIPPED]: { label: "Skipped", style: neutralTag },
  [TaskStatus.DISQUALIFIED]: {
    label: "Disqualified",
    style: { ...neutralTag, color: "#b42318", background: "#fef2f2", borderColor: "#f5c6c0" },
  },
};

const ROLE_LABEL: Record<MemberRole, string> = {
  [MemberRole.OWNER]: "Owner",
  [MemberRole.VERIFIER]: "Verifier",
  [MemberRole.ACTOR]: "Actor",
};

const ROLE_ORDER: MemberRole[] = [MemberRole.OWNER, MemberRole.VERIFIER, MemberRole.ACTOR];

const RESOLVED = new Set<TaskStatus>([TaskStatus.COMPLETE, TaskStatus.SKIPPED, TaskStatus.DISQUALIFIED]);

const DUE_STYLE = {
  overdue: { ...neutralTag, color: "#b42318", background: "#fef2f2", borderColor: "#f5c6c0" },
  dueToday: { ...neutralTag, color: "#b45309", background: "#fffbeb", borderColor: "#fde68a" },
  upcoming: neutralTag,
  none: neutralTag,
} as const;

const DueTag = ({ task }: { task: BoardTask }) => {
  const { state } = dueState(task.dueAt, RESOLVED.has(task.status));
  if (state === "none") return null;

  return (
    <Tooltip title={`Due ${new Date(task.dueAt!).toLocaleDateString()}`}>
      <Tag bordered style={DUE_STYLE[state]}>
        <TbClock className="inline" />
        {describeDue(task.dueAt, false)}
      </Tag>
    </Tooltip>
  );
};

const countStatuses = (statuses: TaskStatus[]): StatusCounts => {
  const done = statuses.filter((s) => s === TaskStatus.COMPLETE).length;
  const skipped = statuses.filter((s) => s === TaskStatus.SKIPPED || s === TaskStatus.DISQUALIFIED).length;
  return { done, skipped, open: statuses.length - done - skipped, total: statuses.length };
};

const flattenTasks = (tasks: BoardTask[] = []): BoardTask[] =>
  tasks.flatMap((task) => [task, ...flattenTasks(task.subtasks)]);

const matchesFilter = (status: TaskStatus, filter: RowFilter) => {
  if (filter === "all") return true;
  if (filter === "done") return RESOLVED.has(status);
  return !RESOLVED.has(status);
};

const hasUnfilledSlot = (task: BoardTask) =>
  (task.members ?? []).some((member) => member.kind !== MemberKind.EXTERNAL && !member.assignee);

const slotLabel = (member?: TaskMemberInstance) => {
  if (!member) return "Not on this task";
  if (member.kind === MemberKind.EXTERNAL) return "The client";
  if (member.assignee) return member.assignee.name;
  if (member.target === MemberTarget.ROLE) {
    const match = TEAM_ROLES.find((option) => option.value === member.assigneeRole);
    return `${match?.label ?? member.assigneeRole ?? "Role"} — not filled on this case`;
  }
  return "Unassigned";
};

const LegendDot = ({ color, label }: { color: string; label: string }) => (
  <span className="flex items-center gap-1.5">
    <span style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }} />
    <Typography.Text style={{ fontSize: 12, color: taskUi.textMuted, fontVariantNumeric: "tabular-nums" }}>
      {label}
    </Typography.Text>
  </span>
);

const StatusControl = ({
  status,
  size = 18,
  onComplete,
}: {
  status: TaskStatus;
  size?: number;
  onComplete?: () => void;
}) => {
  if (status === TaskStatus.COMPLETE) {
    return (
      <span
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          background: DONE_COLOR,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <TbCheck size={size - 7} color="#fff" strokeWidth={3.2} />
      </span>
    );
  }

  if (status === TaskStatus.SKIPPED || status === TaskStatus.DISQUALIFIED) {
    return (
      <span
        style={{
          width: size,
          height: size,
          borderRadius: "50%",
          border: "1.5px solid #c6c6d0",
          background: "#f4f4f7",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <TbMinus size={size - 8} color={taskUi.textFaint} strokeWidth={3} />
      </span>
    );
  }

  if (status === TaskStatus.ACTIVE && onComplete) {
    return (
      <Tooltip title="Mark complete">
        <button
          type="button"
          aria-label="Mark complete"
          onClick={onComplete}
          style={{
            width: size,
            height: size,
            padding: 0,
            borderRadius: "50%",
            border: `1.5px solid ${taskUi.indigo}`,
            background: "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <span style={{ width: size - 11, height: size - 11, borderRadius: "50%", background: taskUi.indigo }} />
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={status === TaskStatus.ACTIVE ? "You are not the actor on this task" : "Not active yet"}>
      <span
        style={{ width: size, height: size, borderRadius: "50%", border: "1.5px solid #bfbfcb", background: "#fff" }}
      />
    </Tooltip>
  );
};

const MemberDot = ({
  role,
  member,
  resolution,
}: {
  role: MemberRole;
  member?: TaskMemberInstance;
  resolution: SlotResolution;
}) => {
  const external = member?.kind === MemberKind.EXTERNAL;
  const isUser = !external && member?.target === MemberTarget.INDIVIDUAL && Boolean(member?.assigneeId);

  const tone = !member
    ? { background: "#ffffff", color: "#c6c6d0", border: "1px dashed #e0e0e8" }
    : external
      ? { background: "#fdf3e7", color: "#97551f", border: "1px solid #ebd8ac" }
      : resolution.filled
        ? {
            background: isUser ? taskUi.indigoSoft : taskUi.purpleSoft,
            color: isUser ? taskUi.indigo : taskUi.purple,
            border: `1px solid ${isUser ? taskUi.indigoLine : taskUi.purpleLine}`,
          }
        : { background: "#ffffff", color: "#b45309", border: "1px dashed #ebd8ac" };

  const Icon =
    member?.target === MemberTarget.ROLE ? TEAM_ROLES.find((r) => r.value === member?.assigneeRole)?.icon : undefined;
  const initials =
    member?.target === MemberTarget.INDIVIDUAL && (member?.assigneeId as any)?.name
      ? getInitials((member?.assigneeId as any).name)
      : undefined;

  return (
    <Tooltip title={`${ROLE_LABEL[role]}: ${resolution.label}`}>
      <span
        style={{
          ...tone,
          width: 22,
          height: 22,
          borderRadius: "50%",
          fontSize: 10,
          fontWeight: 650,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: -5,
          flexShrink: 0,
        }}
      >
        {external ? <TbUserCircle size={14} /> : Icon ? <Icon size={12} /> : (initials ?? ROLE_LABEL[role].charAt(0))}
      </span>
    </Tooltip>
  );
};

type SlotResolution = { member?: TaskMemberInstance; holders: AssignableUser[]; filled: boolean; label: string };

interface TaskBoardTabProps {
  selector: string;
  resource: Resource;
  orgCode: string | undefined;
  /** When supplied, only the task's actor may complete it. */
  currentUserId?: T_MongoId;
}

const TaskBoardTab = ({ selector, resource, orgCode, currentUserId }: TaskBoardTabProps) => {
  const { message } = App.useApp();

  const [selectedBoardId, setSelectedBoardId] = useState<string | undefined>();
  const [filter, setFilter] = useState<RowFilter>("all");
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [outcomeTask, setOutcomeTask] = useState<BoardTask | undefined>();
  const [chosenOutcome, setChosenOutcome] = useState<string | undefined>();
  const [skipTask, setSkipTask] = useState<BoardTask | undefined>();
  const [skipReason, setSkipReason] = useState("");
  const [memberTask, setMemberTask] = useState<BoardTask | undefined>();
  const [memberDraft, setMemberDraft] = useState<Partial<Record<MemberRole, string | undefined>>>({});
  const [userQuery, setUserQuery] = useState("");
  const [userSearch, setUserSearch] = useState("");

  const boardList = ApiService.taskBoard.useGetByApplication(selector, resource);
  const boardDetail = ApiService.taskBoard.useGetById(selectedBoardId);
  const updateStatus = ApiService.taskBoard.useUpdateTaskStatus(selectedBoardId);
  const assignMember = ApiService.taskBoard.useAssignTaskMember(selectedBoardId);
  const instantiate = ApiService.taskBoard.useInstantiate(selector, resource);
  const team = ApiService.application.useGetTeamMembers(selector);
  const [detailTask, setDetailTask] = useState<BoardTask | undefined>();

  const nameOf = (id?: string) => (id ? (teamById.get(id)?.name ?? "Someone") : "System");

  const teamByRole = useMemo(() => {
    const map = new Map<string, AssignableUser[]>();
    (team.data ?? []).forEach(({ user, role }) => map.set(role, [...(map.get(role) ?? []), user]));
    return map;
  }, [team.data]);

  const teamById = useMemo(() => new Map((team.data ?? []).map(({ user }) => [String(user._id), user])), [team.data]);

  const memberOf = (task: BoardTask, role: MemberRole) => task.members?.find((m) => m.role === role);

  const resolveSlot = (task: BoardTask, role: MemberRole): SlotResolution => {
    const member = memberOf(task, role);
    if (!member) return { holders: [], filled: false, label: "Not on this task" };
    if (member.kind === MemberKind.EXTERNAL) return { member, holders: [], filled: true, label: "The client" };

    if (member.target === MemberTarget.ROLE) {
      const name = TEAM_ROLES.find((o) => o.value === member.assigneeRole)?.label ?? member.assigneeRole ?? "Role";
      const holders = teamByRole.get(member.assigneeRole ?? "") ?? [];

      return {
        member,
        holders,
        filled: holders.length > 0,
        label: holders.length
          ? `${name} (${holders.map((u) => u.name).join(", ")})`
          : `${name} (nobody on this case team)`,
      };
    }

    const assigneeUser = (member.assigneeId as unknown as AssignableUser)?._id
      ? (member.assigneeId as unknown as AssignableUser)
      : undefined;

    return {
      member,
      holders: assigneeUser ? [assigneeUser] : [],
      filled: Boolean(assigneeUser),
      label: assigneeUser?.name ?? (member.assigneeId ? "Assigned" : "Unassigned"),
    };
  };

  const unfilledRoles = (task: BoardTask) =>
    ROLE_ORDER.filter((role) => {
      const member = memberOf(task, role);
      if (!member || member.kind === MemberKind.EXTERNAL) return false;
      return !resolveSlot(task, role).filled;
    });

  useEffect(() => {
    const timer = setTimeout(() => setUserSearch(userQuery.trim()), 250);
    return () => clearTimeout(timer);
  }, [userQuery]);

  const usersQuery = ApiService.user.useGetPaginated({ page: 1, limit: 20, search: userSearch, role: [] });
  const assignableMembers = usersQuery.data?.result.data ?? [];

  const templateList = ApiService.taskTemplate.useGetPaginated({
    page: 1,
    limit: 50,
    resource,
    orgCode,
    enabled: boardList.isFetched && boardList.data?.length === 0,
  });

  const board = boardDetail.data as BoardView | undefined;

  useEffect(() => {
    if (boardList.data?.length && !selectedBoardId) {
      setSelectedBoardId(boardList.data[0]._id.toString());
    }
  }, [boardList.data, selectedBoardId]);

  const allTasks = useMemo(() => flattenTasks(board?.groups.flatMap((g) => g.tasks) ?? []), [board]);

  const activeCounts = useMemo(() => countStatuses(allTasks.map((task) => task.status)), [allTasks]);

  const unfilledCount = useMemo(
    () => allTasks.filter((t) => t.status === TaskStatus.ACTIVE && unfilledRoles(t).length).length,
    [allTasks],
  );

  const overdueCount = useMemo(
    () => allTasks.filter((task) => dueState(task.dueAt, RESOLVED.has(task.status)).state === "overdue").length,
    [allTasks],
  );

  // Landing on an empty list is worse than landing on everything.
  useEffect(() => {
    if (!board) return;
    setFilter(activeCounts.open > 0 ? "open" : "all");
  }, [board?._id]);

  // const boardCounts = useMemo(() => {
  //   const map = new Map<string, StatusCounts>();
  //   boardList.data?.forEach((item: TaskBoardListItemDto) => {
  //     map.set(item._id.toString(), countStatuses((item.tasks ?? []).map((t) => t.status)));
  //   });
  //   return map;
  // }, [boardList.data]);
  type ActPermission = "allowed" | "unfilled" | "someoneElse";

  const memberUserId = (member?: TaskMemberInstance) =>
    member?.assignee?._id?.toString() ?? (member?.assigneeId ? String(member.assigneeId) : undefined);

  const permissionFor = (task: BoardTask, role: MemberRole): ActPermission => {
    const member = memberOf(task, role);
    if (!member || member.kind === MemberKind.EXTERNAL) return "allowed";

    if (member.target === MemberTarget.ROLE) {
      const holders = teamByRole.get(member.assigneeRole ?? "") ?? [];
      if (!holders.length) return "unfilled";
      if (!currentUserId) return "allowed";
      return holders.some((user) => String(user._id) === currentUserId.toString()) ? "allowed" : "someoneElse";
    }

    const id = member.assigneeId ? String(member.assigneeId) : undefined;
    if (!id) return "unfilled";
    if (!currentUserId) return "allowed";
    return id === currentUserId.toString() ? "allowed" : "someoneElse";
  };

  const canAct = (task: BoardTask, role: MemberRole) => {
    if (!currentUserId) return true;
    const member = memberOf(task, role);
    if (!member) return true;
    return member.assignee?._id?.toString() === currentUserId.toString();
  };

  const submitStatus = (taskId: T_MongoId, status: TaskStatus, extra?: { outcome?: T_MongoId; reason?: string }) =>
    updateStatus.mutate(
      { taskId, status, ...extra },
      {
        onSuccess: () => void message.success(status === TaskStatus.COMPLETE ? "Task completed" : "Task skipped"),
        onError: () => void message.error("Could not update the task"),
      },
    );

  const startComplete = (task: BoardTask) => {
    const outcomes = task.outcomes ?? [];
    if (outcomes.length > 1) {
      setChosenOutcome(undefined);
      setOutcomeTask(task);
      return;
    }
    submitStatus(task._id, TaskStatus.COMPLETE, outcomes[0] ? { outcome: outcomes[0]._id } : undefined);
  };

  const confirmOutcome = () => {
    if (!outcomeTask || !chosenOutcome) return;
    submitStatus(outcomeTask._id, TaskStatus.COMPLETE, { outcome: chosenOutcome as unknown as T_MongoId });
    setOutcomeTask(undefined);
  };

  const confirmSkip = () => {
    if (!skipTask || !skipReason.trim()) return;
    submitStatus(skipTask._id, TaskStatus.SKIPPED, { reason: skipReason.trim() });
    setSkipTask(undefined);
    setSkipReason("");
  };

  const openMembers = (task: BoardTask) => {
    setUserQuery("");
    setMemberDraft(
      ROLE_ORDER.reduce<Partial<Record<MemberRole, string | undefined>>>((acc, role) => {
        acc[role] = memberOf(task, role)?.assignee?._id.toString();
        return acc;
      }, {}),
    );
    setMemberTask(task);
  };

  const confirmMembers = () => {
    if (!memberTask) return;

    const members: TaskItemMember[] = ROLE_ORDER.filter((role) => memberOf(memberTask, role)).map((role) => {
      const slot = memberOf(memberTask, role) as TaskMemberInstance;

      return {
        role,
        kind: slot.kind,
        target: slot.target,
        assigneeRole: slot.assigneeRole,
        assigneeId: memberDraft[role] ? (memberDraft[role] as unknown as T_MongoId) : undefined,
      };
    });

    assignMember.mutate(
      { taskId: memberTask._id, members },
      {
        onSuccess: () => {
          void message.success("Members updated");
          setMemberTask(undefined);
        },
        onError: () => void message.error("Could not update members"),
      },
    );
  };

  const toggleGroup = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleImport = (templateId: string) =>
    instantiate.mutate(templateId, { onError: () => void message.error("Failed to import task board") });

  if (boardList.isLoading) return <Spin className="flex justify-center py-12" />;

  if (!boardList.data?.length) {
    if (templateList.isLoading) return <Spin className="flex justify-center py-12" />;

    const templates = templateList.data?.result?.data ?? [];

    if (!templates.length) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No task templates have been configured for this organisation"
        />
      );
    }

    return (
      <div className="flex flex-col gap-3 py-2">
        <div className="flex flex-col gap-0.5">
          <Typography.Text strong style={{ fontSize: 14.5 }}>
            Choose a template to create this board
          </Typography.Text>
          <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>
            {templates.length} template{templates.length !== 1 ? "s" : ""} available for this organisation
          </Typography.Text>
        </div>

        <div
          className="overflow-hidden border border-solid bg-white"
          style={{ borderColor: taskUi.border, borderRadius: taskUi.radius }}
        >
          {templates.map((template, i) => (
            <div
              key={template._id.toString()}
              className="flex items-center gap-3 border-0 border-solid"
              style={{ padding: "12px 14px", borderTopWidth: i === 0 ? 0 : 1, borderColor: taskUi.hairline }}
            >
              <span
                className="flex items-center justify-center"
                style={{ width: 32, height: 32, borderRadius: 8, background: taskUi.indigoSoft, flexShrink: 0 }}
              >
                <TbLayoutKanban size={17} color={taskUi.indigo} />
              </span>
              <span className="flex min-w-0 flex-grow flex-col">
                <Typography.Text style={{ fontSize: 13.5 }} ellipsis>
                  {template.name}
                </Typography.Text>
                <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>
                  {template.groups?.length ?? 0} group{(template.groups?.length ?? 0) !== 1 ? "s" : ""} ·{" "}
                  {template.resource}
                </Typography.Text>
              </span>
              <Button
                type="primary"
                size="small"
                loading={instantiate.isPending && instantiate.variables === template._id.toString()}
                onClick={() => handleImport(template._id.toString())}
              >
                Import
              </Button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const renderTask = (task: BoardTask, depth: number) => {
    const meta = STATUS_META[task.status];
    const subtasks = (task.subtasks ?? []).filter((sub) => matchesFilter(sub.status, filter));
    const subCounts = countStatuses((task.subtasks ?? []).map((s) => s.status));
    const outcomeName = task.outcomes?.find((o) => o._id.toString() === task.selectedOutcome?.toString())?.name;
    const isSub = depth > 0;
    const isResolved = RESOLVED.has(task.status);
    const actionable = task.status === TaskStatus.ACTIVE && canAct(task, MemberRole.ACTOR);
    const busy = updateStatus.isPending && updateStatus.variables?.taskId?.toString() === task._id.toString();
    const slots = (task.members ?? []).length;
    const missing = unfilledRoles(task);
    const unfilled = task.status === TaskStatus.ACTIVE && missing.length > 0;

    const secondary =
      task.status === TaskStatus.SKIPPED && task.skipReason
        ? `Skipped — ${task.skipReason}`
        : outcomeName
          ? `Outcome: ${outcomeName}`
          : unfilled
            ? `Waiting on ${missing.map((r) => resolveSlot(task, r).label).join(" · ")}`
            : subCounts.total > 0
              ? `${subCounts.done} of ${subCounts.total} sub-tasks done`
              : task.description;

    const actorPermission = permissionFor(task, MemberRole.ACTOR);
    const ownerPermission = permissionFor(task, MemberRole.OWNER);
    const isActive = task.status === TaskStatus.ACTIVE;

    const onControl = !isActive
      ? undefined
      : actorPermission === "allowed"
        ? () => startComplete(task)
        : actorPermission === "unfilled"
          ? () => openMembers(task)
          : undefined;

    return (
      <div key={task._id.toString()}>
        <div
          className="flex items-center border-0 border-b border-solid"
          style={{
            minHeight: isSub ? taskUi.subRowHeight : taskUi.rowHeight,
            paddingRight: 14,
            borderColor: taskUi.hairline,
            background: isSub ? taskUi.subtle : undefined,
          }}
        >
          {isSub && (
            <>
              <span style={{ width: 28, flexShrink: 0 }} />
              <span style={{ width: 1, alignSelf: "stretch", background: "#e8e8ee" }} />
            </>
          )}

          <span
            className="flex items-center justify-center"
            style={{
              width: isSub ? 36 : 40,
              minHeight: isSub ? taskUi.subRowHeight : taskUi.rowHeight,
              flexShrink: 0,
            }}
          >
            {busy ? (
              <Spin size="small" />
            ) : (
              <StatusControl
                status={task.status}
                size={isSub ? 15 : 18}
                onComplete={actionable ? () => startComplete(task) : undefined}
              />
            )}
          </span>

          <span className="flex min-w-0 flex-grow flex-col" style={{ paddingTop: 5, paddingBottom: 5 }}>
            <button
              type="button"
              onClick={() => setDetailTask(task)}
              className="flex min-w-0 flex-grow flex-col border-0 bg-transparent p-0 text-left"
              style={{ paddingTop: 5, paddingBottom: 5, cursor: "pointer" }}
            >
              <Typography.Text
                style={{
                  fontSize: isSub ? 13 : 13.5,
                  color: isResolved ? taskUi.textFaint : taskUi.text,
                  textDecoration: task.status === TaskStatus.SKIPPED ? "line-through" : undefined,
                }}
                ellipsis
              >
                {task.name}
              </Typography.Text>
              {secondary && (
                <Typography.Text style={{ fontSize: 11.5, color: unfilled ? "#b45309" : taskUi.textFaint }} ellipsis>
                  {secondary}
                </Typography.Text>
              )}
            </button>
          </span>

          <span className="flex flex-shrink-0 items-center gap-3">
            <DueTag task={task} />

            {task.status !== TaskStatus.COMPLETE && (
              <Tag bordered style={meta.style}>
                {meta.label}
              </Tag>
            )}

            <span className="flex items-center justify-end" style={{ width: 66 }}>
              {slots > 0 && (
                <button
                  type="button"
                  // onClick={() => openMembers(task)}
                  aria-label="Manage members"
                  className="flex items-center border-0 bg-transparent p-0"
                  style={{ cursor: "pointer", paddingLeft: 5 }}
                >
                  {ROLE_ORDER.map((role) => (
                    <MemberDot
                      key={role}
                      role={role}
                      member={memberOf(task, role)}
                      resolution={resolveSlot(task, role)}
                    />
                  ))}
                </button>
              )}
            </span>

            <span className="flex items-center justify-end gap-1" style={{ width: 132 }}>
              {task.status === TaskStatus.ACTIVE && (
                <>
                  {actorPermission === "allowed" && (
                    <Tooltip title={`Mark completed${task.subtasks?.length ? " including subtasks" : ""}`}>
                      <Button
                        type="text"
                        size="small"
                        icon={task.subtasks?.length ? <TbChecks size={14} /> : <TbCheck size={14} />}
                        loading={busy}
                        onClick={() => startComplete(task)}
                        style={{ color: DONE_COLOR, fontSize: 12.5 }}
                      />
                    </Tooltip>
                  )}

                  {actorPermission === "unfilled" && (
                    <Button
                      type="text"
                      size="small"
                      icon={<TbUserPlus size={14} />}
                      onClick={() => openMembers(task)}
                      style={{ color: "#b45309", fontSize: 12.5 }}
                    >
                      Assign actor
                    </Button>
                  )}

                  {actorPermission === "someoneElse" && (
                    <Tooltip title={`${slotLabel(memberOf(task, MemberRole.ACTOR))} has this task`}>
                      <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>Not yours</Typography.Text>
                    </Tooltip>
                  )}

                  {ownerPermission !== "someoneElse" && (
                    <Tooltip title="Skip with a reason">
                      <Button
                        type="text"
                        size="small"
                        aria-label="Skip task"
                        icon={<TbPlayerSkipForward size={15} />}
                        onClick={() => {
                          setSkipReason("");
                          setSkipTask(task);
                        }}
                        style={{ color: "#9a9aa5" }}
                      />
                    </Tooltip>
                  )}
                </>
              )}
            </span>
          </span>
        </div>

        {subtasks.map((sub) => renderTask(sub, depth + 1))}
      </div>
    );
  };

  const pct = (n: number) => (activeCounts.total === 0 ? 0 : (n / activeCounts.total) * 100);

  return (
    <div className="flex flex-col gap-3">
      <BoardSyncBanner selector={selector} resource={resource} />

      {boardDetail.isLoading ? (
        <Spin className="flex justify-center py-12" />
      ) : !board?.groups?.length ? (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No groups in this board" />
      ) : (
        <>
          <div
            className="flex flex-col gap-3 border border-solid bg-white"
            style={{ borderColor: taskUi.border, borderRadius: taskUi.radius, padding: "14px 16px" }}
          >
            <div className="flex items-start gap-4">
              <div className="flex min-w-0 flex-grow flex-col gap-0.5">
                {boardList.data.length > 1 ? (
                  <Select
                    variant="borderless"
                    value={selectedBoardId}
                    onChange={setSelectedBoardId}
                    popupMatchSelectWidth={false}
                    className="w-fit"
                    suffixIcon={<TbChevronDown />}
                    style={{ marginLeft: -11, fontSize: 15, fontWeight: 650 }}
                    options={boardList.data.map((item: TaskBoardListItemDto) => {
                      // const counts = boardCounts.get(item._id.toString());
                      return {
                        value: item._id.toString(),
                        label: item.name,
                        // label: (
                        //   <span className="flex items-center gap-2">
                        //     {item.name}
                        //     {counts && (
                        //       <span
                        //         style={{
                        //           fontSize: 12,
                        //           fontWeight: 400,
                        //           color: taskUi.textFaint,
                        //           fontVariantNumeric: "tabular-nums",
                        //         }}
                        //       >
                        //         {counts.done}/{counts.total}
                        //       </span>
                        //     )}
                        //   </span>
                        // ),
                      };
                    })}
                  />
                ) : (
                  <Typography.Text strong style={{ fontSize: 15 }}>
                    {board.name}
                  </Typography.Text>
                )}

                <Typography.Text style={{ fontSize: 12, color: taskUi.textFaint, fontVariantNumeric: "tabular-nums" }}>
                  {board.groups.length} group{board.groups.length === 1 ? "" : "s"} · {activeCounts.total} task
                  {activeCounts.total === 1 ? "" : "s"}
                </Typography.Text>
              </div>

              <Segmented
                size="small"
                value={filter}
                onChange={(value) => setFilter(value as RowFilter)}
                options={[
                  { value: "open", label: `Open ${activeCounts.open}` },
                  { value: "done", label: `Resolved ${activeCounts.done + activeCounts.skipped}` },
                  { value: "all", label: `All ${activeCounts.total}` },
                ]}
              />
            </div>

            <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", background: OPEN_COLOR }}>
              <div
                style={{ width: `${pct(activeCounts.done)}%`, background: DONE_COLOR, transition: "width 300ms ease" }}
              />
              <div
                style={{
                  width: `${pct(activeCounts.skipped)}%`,
                  background: SKIPPED_COLOR,
                  transition: "width 300ms ease",
                }}
              />
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <LegendDot color={DONE_COLOR} label={`${activeCounts.done} done`} />
              <LegendDot color={SKIPPED_COLOR} label={`${activeCounts.skipped} skipped`} />
              <LegendDot color={OPEN_COLOR} label={`${activeCounts.open} open`} />

              <div className="flex-1" />

              {unfilledCount > 0 && (
                <Button
                  size="small"
                  icon={<TbAlertTriangle size={13} />}
                  onClick={() => setFilter("open")}
                  style={{ color: "#b45309", borderColor: "#ebd8ac", background: "#fffbeb" }}
                >
                  {unfilledCount} waiting on an unfilled role
                </Button>
              )}
            </div>
          </div>

          <div
            className="overflow-hidden border border-solid bg-white"
            style={{ borderColor: taskUi.border, borderRadius: taskUi.radius }}
          >
            {board.groups.map((group) => {
              const groupId = group._id.toString();
              const isCollapsed = collapsed.has(groupId);
              const counts = countStatuses(flattenTasks(group.tasks).map((t) => t.status));
              const visible = group.tasks.filter(
                (task) =>
                  matchesFilter(task.status, filter) ||
                  (task.subtasks ?? []).some((sub) => matchesFilter(sub.status, filter)),
              );

              return (
                <div key={groupId}>
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupId)}
                    aria-expanded={!isCollapsed}
                    className="flex w-full items-center gap-2 border-0 border-b border-solid text-left"
                    style={{
                      height: taskUi.groupHeaderHeight,
                      paddingLeft: 14,
                      background: "#f4f4f8",
                      borderColor: "#e8e8ee",
                      cursor: "pointer",
                    }}
                  >
                    {isCollapsed ? (
                      <TbChevronRight size={13} color="#6b6b76" />
                    ) : (
                      <TbChevronDown size={13} color="#6b6b76" />
                    )}
                    <Typography.Text style={{ fontSize: 12.5, fontWeight: 600, color: "#33333b" }}>
                      {group.name}
                    </Typography.Text>
                    <span
                      style={{ fontSize: 11, color: taskUi.textFaint, fontFamily: "ui-monospace, Menlo, monospace" }}
                    >
                      {group.code}
                    </span>
                    <Typography.Text
                      style={{ fontSize: 12, color: taskUi.textFaint, fontVariantNumeric: "tabular-nums" }}
                    >
                      {counts.done} of {counts.total}
                      {counts.skipped > 0 ? ` · ${counts.skipped} skipped` : ""}
                    </Typography.Text>
                  </button>

                  {!isCollapsed &&
                    (visible.length === 0 ? (
                      <div style={{ padding: "12px 14px" }}>
                        <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>
                          {group.tasks.length === 0 ? "No tasks in this group" : "Nothing matches this filter"}
                        </Typography.Text>
                      </div>
                    ) : (
                      visible.map((task) => renderTask(task, 0))
                    ))}
                </div>
              );
            })}
          </div>
        </>
      )}
      <Modal
        open={!!outcomeTask}
        title="Choose an outcome"
        okText="Complete task"
        okButtonProps={{ disabled: !chosenOutcome, loading: updateStatus.isPending }}
        onOk={confirmOutcome}
        onCancel={() => setOutcomeTask(undefined)}
        destroyOnHidden
      >
        <Typography.Paragraph style={{ fontSize: 12.5, color: taskUi.textFaint }}>
          {outcomeTask?.name}
        </Typography.Paragraph>
        <Radio.Group
          value={chosenOutcome}
          onChange={(e) => setChosenOutcome(e.target.value)}
          className="flex w-full flex-col gap-2"
        >
          {outcomeTask?.outcomes?.map((outcome) => (
            <Radio
              key={outcome._id.toString()}
              value={outcome._id.toString()}
              className="m-0 w-full border border-solid"
              style={{ borderColor: "#e8e8ee", borderRadius: 8, padding: "10px 12px" }}
            >
              <span className="flex flex-col">
                <Typography.Text style={{ fontSize: 13 }}>{outcome.name}</Typography.Text>
                {outcome.description && (
                  <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>
                    {outcome.description}
                  </Typography.Text>
                )}
              </span>
            </Radio>
          ))}
        </Radio.Group>
      </Modal>
      <Modal
        open={!!skipTask}
        title="Skip this task"
        okText="Skip task"
        okButtonProps={{ disabled: !skipReason.trim(), loading: updateStatus.isPending, danger: true }}
        onOk={confirmSkip}
        onCancel={() => setSkipTask(undefined)}
        destroyOnHidden
      >
        <Typography.Paragraph style={{ fontSize: 12.5, color: taskUi.textFaint }}>
          {skipTask?.name} — the reason is stored on the task and shown to anyone reviewing this board.
        </Typography.Paragraph>
        <Input.TextArea
          rows={3}
          value={skipReason}
          maxLength={280}
          showCount
          placeholder="Why is this task not required?"
          onChange={(e) => setSkipReason(e.target.value)}
        />
      </Modal>
      <Modal
        open={!!memberTask}
        title="Task members"
        okText="Save"
        okButtonProps={{ loading: assignMember.isPending }}
        onOk={confirmMembers}
        onCancel={() => setMemberTask(undefined)}
        destroyOnHidden
      >
        <Typography.Paragraph style={{ fontSize: 12.5, color: taskUi.textFaint }}>
          {memberTask?.name}
        </Typography.Paragraph>

        <div className="flex flex-col gap-4 pt-1">
          {ROLE_ORDER.map((role) => {
            const slot = memberTask ? memberOf(memberTask, role) : undefined;

            if (!slot) {
              return (
                <div key={role} className="flex flex-col gap-1">
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {ROLE_LABEL[role]}
                  </Typography.Text>
                  <Typography.Text style={{ fontSize: 12, color: taskUi.textFaint }}>
                    The template does not put a {ROLE_LABEL[role].toLowerCase()} on this task.
                  </Typography.Text>
                </div>
              );
            }

            const isClient = slot.kind === MemberKind.EXTERNAL;
            const teamRole =
              slot.target === MemberTarget.ROLE && !isClient
                ? (TEAM_ROLES.find((option) => option.value === slot.assigneeRole)?.label ?? slot.assigneeRole)
                : undefined;

            return (
              <div key={role} className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {ROLE_LABEL[role]}
                  </Typography.Text>
                  {teamRole && (
                    <Tag bordered style={neutralTag}>
                      template expects {teamRole}
                    </Tag>
                  )}
                  {isClient && (
                    <Tag
                      bordered
                      style={{ ...neutralTag, color: "#97551f", background: "#fdf3e7", borderColor: "#ebd8ac" }}
                    >
                      the client
                    </Tag>
                  )}
                </div>

                {isClient ? (
                  <Typography.Text style={{ fontSize: 12, color: taskUi.textFaint }}>
                    Filled by the client on this application — nothing to assign here.
                  </Typography.Text>
                ) : (
                  <Select
                    allowClear
                    showSearch
                    filterOption={false}
                    placeholder={`Assign ${ROLE_LABEL[role].toLowerCase()}`}
                    value={memberDraft[role]}
                    onSearch={setUserQuery}
                    onChange={(value) => setMemberDraft((prev) => ({ ...prev, [role]: value }))}
                    loading={usersQuery.isFetching}
                    notFoundContent={usersQuery.isFetching ? <Spin size="small" /> : "No one found"}
                    options={assignableMembers.map((user: ResolvedAssignee) => ({
                      value: user._id.toString(),
                      label: user.email ? `${user.name} · ${user.email}` : user.name,
                    }))}
                    suffixIcon={<TbUserPlus size={14} />}
                  />
                )}
              </div>
            );
          })}
        </div>
      </Modal>
      <TaskDrawer open={!!detailTask} task={detailTask} onClose={() => setDetailTask(undefined)} nameOf={nameOf} />
    </div>
  );
};

export default TaskBoardTab;
